/*
 * <motion-toggle aria-controls="ID"> wraps a <button> that lets visitors pause and resume moving content
 * (WCAG 2.2.2 Pause, Stop, Hide). Supports:
 *  - autoplaying carousels (e.g. <announcement-bar>), which expose a `player`
 *  - <marquee-text>, which exposes pause() / play()
 *
 * The button must contain two icons (pause first, play second) and a <span data-label> for the accessible name.
 * Labels are read from data-pause-label / data-play-label on <motion-toggle>.
 */
if (!window.customElements.get('motion-toggle')) {
  class MotionToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => (this.paused ? this.play() : this.pause()));
    }

    get target() {
      return document.getElementById(this.getAttribute('aria-controls'));
    }

    get paused() {
      return this.hasAttribute('paused');
    }

    pause() {
      const target = this.target;
      if (!target) return;

      this.setAttribute('paused', '');

      if ('player' in target) {
        const player = target.player;
        if (player) {
          player.pause();
          this.#keepPlayerPaused(player);
        }
      } else if (typeof target.pause === 'function') {
        target.pause();
      }

      this.#render();
    }

    play() {
      const target = this.target;
      if (!target) return;

      this.removeAttribute('paused');

      if ('player' in target) {
        target.player?.resume();
      } else if (typeof target.play === 'function') {
        target.play();
      }

      this.#render();
    }

    // The carousel restarts its player after manual navigation or when the tab becomes visible again, so we
    // re-pause it for as long as the visitor wants it paused.
    #keepPlayerPaused(player) {
      if (this._boundPlayer === player) return;
      this._boundPlayer = player;

      const repause = () => {
        if (this.paused) player.pause();
      };

      player.addEventListener('player:start', repause);
      player.addEventListener('player:resume', repause);
    }

    #render() {
      const [pauseIcon, playIcon] = this.button.querySelectorAll('svg');
      pauseIcon?.classList.toggle('hidden', this.paused);
      playIcon?.classList.toggle('hidden', !this.paused);

      const label = this.button.querySelector('[data-label]');
      if (label) {
        label.textContent = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      }
    }
  }

  window.customElements.define('motion-toggle', MotionToggle);
}
